/**
 * Visit Detail View
 * Display complete visit information
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Edit, Trash2, ArrowLeft, Calendar, Clock, User, ClipboardCheck } from 'lucide-react'

import { useVisit, useDeleteVisit } from '@/hooks/useVisits'
import { toast } from '@/lib/toast'
import { PageLoading } from '@/components/loading'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { VISIT_STATUS_LABELS } from '@/types/visit'
import { VISIT_DECISION_LABELS, mapTypeIdsToClassifications } from '@/constants/classification-types'
import { getAssistanceLabelFromTypeId } from '@/constants/assistance-types'

export function VisitDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Fetch visit data
  const { data: visit, isLoading, error } = useVisit(id!)

  // Delete mutation
  const deleteVisit = useDeleteVisit()

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteVisit.mutateAsync(id!)
      toast.success('Visit deleted successfully')
      navigate('/visits')
    } catch (error: any) {
      toast.error('Failed to delete visit', error.message)
    }
  }

  // Format date
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Format time
  const formatTime = (timeString?: string | null): string => {
    if (!timeString) return 'N/A'
    return timeString.substring(0, 5) // HH:MM from HH:MM:SS
  }

  // Format datetime
  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get client name from client_info
  const getClientName = (): string => {
    if (!visit || !visit.client_info) return 'N/A'
    const { firstname, lastname, middlename } = visit.client_info
    if (!firstname && !lastname) return 'N/A'
    const middle = middlename ? ` ${middlename} ` : ' '
    return `${firstname || ''}${middle}${lastname || ''}`.trim()
  }

  // Get lawyer name with Atty. prefix
  const getLawyerName = (): string => {
    if (!visit || !visit.lawyer_name) return 'Unassigned'
    return `Atty. ${visit.lawyer_name}`
  }

  // Format field name to readable label
  const formatFieldLabel = (key: string): string => {
    // Special cases
    const labelMap: Record<string, string> = {
      firstname: 'First Name',
      middlename: 'Middle Name',
      lastname: 'Last Name',
      birthday: 'Birthday',
      gender: 'Gender',
      civil_status: 'Civil Status',
      citizenship: 'Citizenship',
      monthly_income: 'Monthly Income',
      educational_attainment: 'Educational Attainment',
      indigenous_group: 'Indigenous Group',
      street_name: 'Street',
      barangay: 'Barangay',
      city: 'City',
      province: 'Province',
      postal_code: 'Postal Code',
      contact_number: 'Contact Number',
      email: 'Email',
      religion: 'Religion',
    }

    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  // Format field value for display
  const formatFieldValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === '') return 'N/A'

    // Special formatting
    if (key === 'birthday' && typeof value === 'string') {
      return formatDate(value)
    }
    if (key === 'monthly_income' && typeof value === 'number') {
      return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }

    return String(value)
  }

  if (isLoading) {
    return <PageLoading message="Loading visit details..." />
  }

  if (error || !visit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-red-600">Visit not found</p>
          <Link to="/visits">
            <Button className="mt-4 bg-primary">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Visits
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/visits">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-primary">Visit Details</h1>
            {visit.control_number && (
              <p className="text-gray-600 mt-1 font-mono text-sm">
                Control Number: {visit.control_number}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/visits/${id}/edit`}>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Visit Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5" />
          Visit Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Visit Date" value={formatDate(visit.visit_date)} />
          <InfoItem
            label="Status"
            value={
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  visit.status === 1
                    ? 'bg-blue-100 text-blue-800'
                    : visit.status === 2
                    ? 'bg-yellow-100 text-yellow-800'
                    : visit.status === 3
                    ? 'bg-green-100 text-green-800'
                    : visit.status === 4
                    ? 'bg-red-100 text-red-800'
                    : visit.status === 101
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {visit.status === 101 ? 'Denied' : visit.status ? VISIT_STATUS_LABELS[visit.status] : 'Unknown'}
              </span>
            }
          />
          <InfoItem label="Client Name" value={getClientName()} />
          <InfoItem label="Office" value={visit.office_name || 'N/A'} />
          <InfoItem label="Lawyer Assigned" value={getLawyerName()} />
        </div>
      </div>

      {/* Time Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5" />
          Time Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Time In" value={formatTime(visit.time_in)} />
          <InfoItem label="Time Out" value={formatTime(visit.time_out)} />
        </div>
      </div>

      {/* Classification */}
      {visit.classifications && visit.classifications.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <ClipboardCheck className="h-5 w-5" />
            Classification
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visit.classifications.map((classification) => {
              const typeIds: number[] = classification.detail?.types || []
              const labels = mapTypeIdsToClassifications(typeIds)
              return (
                <div key={classification.id} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem
                    label="Decision"
                    value={VISIT_DECISION_LABELS[classification.classification_id] || 'N/A'}
                  />
                  <InfoItem
                    label="Client Classifications"
                    value={
                      labels.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {labels.map((label) => (
                            <span
                              key={label}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        'None'
                      )
                    }
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Client Information */}
      {visit.client_info && Object.keys(visit.client_info).length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
            <User className="h-5 w-5" />
            Client Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(visit.client_info)
              .filter(([_, value]) => value !== null && value !== undefined && value !== '')
              .map(([key, value]) => (
                <InfoItem
                  key={key}
                  label={formatFieldLabel(key)}
                  value={formatFieldValue(key, value)}
                />
              ))}
          </div>
        </div>
      )}

      {/* Assistance Details */}
      {visit.transactions && visit.transactions.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold text-primary mb-4">Assistance Details</h2>
          <div className="space-y-6">
            {visit.transactions.map((transaction) => (
              <div key={transaction.id} className="border-b last:border-b-0 pb-6 last:pb-0">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {getAssistanceLabelFromTypeId(transaction.transaction_type_id)}
                  {transaction.control_number && (
                    <span className="ml-2 text-sm font-mono text-gray-600">
                      ({transaction.control_number})
                    </span>
                  )}
                </h3>
                {transaction.transaction_detail && Object.keys(transaction.transaction_detail).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(transaction.transaction_detail)
                      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                      .map(([key, value]) => (
                        <InfoItem
                          key={key}
                          label={formatFieldLabel(key)}
                          value={formatFieldValue(key, value)}
                        />
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this visit? This action cannot be undone
              and will also delete any related classifications and transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Helper component for displaying information
function InfoItem({
  label,
  value,
  className = '',
}: {
  label: string
  value: React.ReactNode
  className?: string
}) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className={`mt-1 text-sm text-gray-900 ${className}`}>
        {value || 'N/A'}
      </dd>
    </div>
  )
}

export default VisitDetail
